import React, { Component } from 'react';
import PropTypes from 'prop-types';

import {
    pick, omit, includes, intersection, isEmpty, has,
} from 'lodash';

import withMessages from 'component-utilities/intl';

import Accordion from 'v-c/Accordions/Accordion';
import AccordionItem from 'v-c/Accordions/AccordionItem';

import reactForm from 'component-utilities/form/reactForm';
import emailValidator from 'component-lib/emailValidator';
import passwordValidator from 'component-utilities/passwordValidator/passwordValidator';

import toggleInArray from 'component-lib/toggleInArray';

import PageContent from 'v-c/Templates/Components/PageContent';
import ContentHeader from 'v-c/Templates/Components/ContentHeader/ContentHeader';
import ContentTitle from 'v-c/Templates/Components/ContentHeader/ContentTitle';
import ContentBody from 'v-c/Templates/Components/ContentBody';
import LoadingIndicator from 'v-c/LoadingIndicator/Indicator';
import Actions, { ACTIONS } from 'v-c/Buttons/ActionButtons/Actions';

import { FormButtons } from 'v-c/Forms';
import { PrimaryButton, SecondaryButton, Spacer } from 'v-c/Buttons/Components';

import BasicInfoTab from './tabs/BasicInfoTab';
import EmailsTab from './tabs/EmailsTab';
import PasswordTab from './tabs/PasswordTab';
import RolesGroupsStatusTab from './tabs/RolesGroupsStatusTab';

import populateHandles from './components/populateHandles';

import defaults from './defaults.json';

const { TABS, TYPES, FORM_ERRORS } = defaults;

export { TABS, TYPES };

const tabComponents = {
    [TABS.BASIC_INFO]: BasicInfoTab,
    [TABS.EMAILS]: EmailsTab,
    [TABS.PASSWORD]: PasswordTab,
    [TABS.ROLES_GROUPS_STATUS]: RolesGroupsStatusTab,
};

const BASE_KEY = 'components.templates.edit_user';
const ISSUES_BASE_KEY = 'components.validation_issues';

const { validatePassword } = passwordValidator();

@reactForm({
    form: 'user-form',
    fields: [
        'id',
        'active',
        'isLocked',
        'username',
        'firstName',
        'lastName',
        'email',
        'confirmEmail',
        'handles',
        'password',
        'confirmPassword',
        'roles',
        'sendWelcomeEmail',
    ],
})
@withMessages
class AccordionView extends Component {
    static propTypes = {
        Breadcrumb: PropTypes.any.isRequired,
        user: PropTypes.object.isRequired,
        deleteActionConfig: PropTypes.shape({
            onDelete: PropTypes.func.isRequired,
            showModal: PropTypes.func.isRequired,
            text: PropTypes.string,
        }),
        reactForm: PropTypes.shape({
            registerFieldsChangeCallback: PropTypes.func.isRequired,
            setInitialFields: PropTypes.func.isRequired,
            values: PropTypes.object.isRequired,
            changedFieldValues: PropTypes.object.isRequired,
            fields: PropTypes.object.isRequired,
            isDirtyFields: PropTypes.bool.isRequired,
        }).isRequired,
        onSave: PropTypes.func.isRequired,
        onCancel: PropTypes.func.isRequired,
        resetUserErrors: PropTypes.func.isRequired,
        type: PropTypes.oneOf(Object.keys(TYPES).map(k => TYPES[k])).isRequired,
        loggedInAsUserId: PropTypes.number.isRequired,
        getMessage: PropTypes.func.isRequired,
        loginAttemptRestrictionEnabled: PropTypes.bool,
        userHasAdministratorRights: PropTypes.bool.isRequired,
        shouldRenderContent: PropTypes.bool.isRequired,
        titleConfig: PropTypes.object,
        isHandlesStatusEnabled: PropTypes.bool,
        tabs: PropTypes.array.isRequired,
        delegateDirtyState: PropTypes.func.isRequired,
        isFieldDisabled: PropTypes.bool,
    };

    static defaultProps = {
        titleConfig: null,
        deleteActionConfig: null,
        loginAttemptRestrictionEnabled: false,
        isHandlesStatusEnabled: false,
        isFieldDisabled: false,
    };

    static getDerivedStateFromProps(nextProps, prevState) {
        if (
            nextProps.user.errors.alreadyTaken &&
      !!nextProps.user.errors.alreadyTaken.length &&
      prevState.justSubmitted
        ) {
            return { justSubmitted: false };
        }

        return null;
    }

    constructor(props) {
        super(props);

        this.state = {
            openedTabs: [TABS.BASIC_INFO, TABS.EMAILS, TABS.PASSWORD, TABS.ROLES_GROUPS_STATUS],
            justSubmitted: false,
        };

        props.reactForm.registerFieldsChangeCallback(this.onFormChange);

        if (props.shouldRenderContent) {
            this.setInitialFields();
        }
    }

    componentDidUpdate(prevProps) {
        const { user, shouldRenderContent } = this.props;

        this.props.delegateDirtyState(this.isComponentDirty);

        if (
            (!prevProps.shouldRenderContent && shouldRenderContent) ||
      (prevProps.user.isProcessing && !user.isProcessing && !Object.keys(user.errors).length)
        ) {
            this.setInitialFields();
        }
    }

    onSaveUser = () => {
        const { user } = this.props.user;
        let saveValues;

        if (this.isNewUserForm) {
            saveValues = pick(this.props.reactForm.values, [
                'username',
                'firstName',
                'lastName',
                'email',
                'password',
                'roles',
                'handles',
                'active',
                'sendWelcomeEmail',
            ]);
        } else if (this.isEditUserForm) {
            saveValues = omit({ ...this.props.reactForm.changedFieldValues }, [
                'confirmPassword',
                'confirmEmail',
            ]);

            if (!saveValues.handles && saveValues.email) {
                saveValues.handles = this.props.reactForm.values.handles;
            }
        } else {
            saveValues = pick(this.props.reactForm.values, 'password');
        }

        this.setState({ justSubmitted: true });

        const formDataToSubmit = omit(saveValues, 'isLocked');

        // When only the sendWelcomeEmail value changed on the form
        //   then we should add the firstName value to the request data
        if (formDataToSubmit.sendWelcomeEmail && Object.keys(saveValues).length === 1) {
            formDataToSubmit.firstName = user.firstName;
        }

        if (saveValues.handles) {
            formDataToSubmit.handles = populateHandles(saveValues.handles);
        }

        const shouldDeleteFailedUserLoginAttempts = has(saveValues, 'isLocked') && saveValues.isLocked !== user.isLocked;

        this.props.onSave(formDataToSubmit, shouldDeleteFailedUserLoginAttempts);
    };

    onDeleteUser = () => {
        const { user } = this.props.user;

        this.setState({ justSubmitted: true });
        this.props.deleteActionConfig.onDelete(user);
    };

    onFormChange = () => {
        this.setState({ justSubmitted: false });

        this.props.resetUserErrors();
    };

    setTabs = tab => () => {
        this.setState({
            openedTabs: toggleInArray(this.state.openedTabs, tab),
        });
    };

    setInitialFields = () => {
        const userData = { ...this.props.user.user };

        if (!this.isNewUserForm) {
            userData.sendWelcomeEmail = false;
        }

        this.props.reactForm.setInitialFields({
            ...this.props.user.user,
            ...(!this.isNewUserForm && { sendWelcomeEmail: false }),
            handles: this.validatedHandles,
        });
    };

    get validatedHandles() {
        return this.props.user.user.handles
            .filter(({ handle }) => emailValidator(handle || ''))
            .map(handle => ({ ...handle, confirmHandle: handle.handle }));
    }

    get isNewUserForm() {
        return this.props.type === TYPES.NEW_USER;
    }

    get isEditUserForm() {
        return this.props.type === TYPES.EDIT_USER;
    }

    get isChangePasswordForm() {
        return this.props.type === TYPES.CHANGE_PASSWORD;
    }

    get usernameError() {
        const { errors } = this.props.user;

        return includes(errors.alreadyTaken, 'username') ? `${ISSUES_BASE_KEY}.already_taken` : null;
    }

    get emailError() {
        const {
            user: { errors },
            reactForm: {
                fields: { email, confirmEmail },
            },
        } = this.props;

        if (!email || !confirmEmail) {
            return null;
        }

        const isEmailStartedToFill = email.value.length && confirmEmail.value.length;

        const hasDuplicateError = !!intersection(['handle', 'email'], errors.alreadyTaken).length;

        if (!isEmailStartedToFill) {
            return `${ISSUES_BASE_KEY}.${FORM_ERRORS.EMPTY_ERROR}`;
        } if (hasDuplicateError) {
            return `${ISSUES_BASE_KEY}.${FORM_ERRORS.ALREADY_TAKEN}`;
        } if (email.value !== confirmEmail.value) {
            return `${ISSUES_BASE_KEY}.${FORM_ERRORS.DOES_NOT_MATCH}`;
        } if (!emailValidator(email.value)) {
            return `${ISSUES_BASE_KEY}.${FORM_ERRORS.INVALID_EMAIL}`;
        }

        return null;
    }

    get handlesError() {
        const { handles } = this.props.reactForm.values;

        if (!handles || !handles.length) {
            return [];
        }

        return handles.map(({ handle, confirmHandle }) => {
            const isHandleStartedToFill = handle.length || confirmHandle.length;

            if (!isHandleStartedToFill) {
                return `${ISSUES_BASE_KEY}.${FORM_ERRORS.EMPTY_ERROR}`;
            } if (handle !== confirmHandle) {
                return `${ISSUES_BASE_KEY}.${FORM_ERRORS.DOES_NOT_MATCH}`;
            } if (!emailValidator(handle)) {
                return `${ISSUES_BASE_KEY}.${FORM_ERRORS.INVALID_EMAIL}`;
            }

            return null;
        });
    }

    get passwordError() {
        if (!Object.keys(this.props.reactForm.values).length) {
            return null;
        }

        const {
            values: { password, confirmPassword, username },
        } = this.props.reactForm;

        const { isValidPassword } = validatePassword(password);
        const isStartedToSetPassword = !!password.length && !!confirmPassword.length;

        if (username.toLowerCase() === password.toLowerCase()) {
            return `${ISSUES_BASE_KEY}.username_password_match`;
        } if (!isStartedToSetPassword) {
            return `${ISSUES_BASE_KEY}.${FORM_ERRORS.EMPTY_ERROR}`;
        } if (isValidPassword && password !== confirmPassword) {
            return `${ISSUES_BASE_KEY}.${FORM_ERRORS.DOES_NOT_MATCH}`;
        } if (!isValidPassword) {
            return `${ISSUES_BASE_KEY}.password_complexity`;
        }

        return null;
    }

    get passwordFieldStartedToFill() {
        const { password, confirmPassword } = this.props.reactForm.values;

        return !!password.length || !!confirmPassword.length;
    }

    get isSaveButtonEnabled() {
        const {
            user: { isProcessing },
            reactForm: { values, isDirtyFields },
        } = this.props;

        if (isEmpty(values)) {
            return null;
        }

        const isEmptyFields = [
            'username',
            'firstName',
            'lastName',
            'email',
            'confirmEmail',
            'roles',
        ].some(field => !values[field].length);

        if (
            isDirtyFields &&
      !isEmptyFields &&
      !isProcessing &&
      !this.usernameError &&
      !this.emailError &&
      !this.handlesError.some(handle => !!handle && handle !== `${ISSUES_BASE_KEY}.${FORM_ERRORS.EMPTY_ERROR}`,) &&
      (this.isNewUserForm || !this.passwordFieldStartedToFill || !this.passwordError)
        ) {
            return true;
        }

        return false;
    }

    get isCancelButtonEnabled() {
        return !this.props.user.isProcessing;
    }

    get fieldsStatus() {
        return this.props.isFieldDisabled ? 'disabled' : '';
    }

    get tabs() {
        const { getMessage, tabs } = this.props;

        const visibleTabs = tabs.map((tab) => {
            const ContentComponent = tabComponents[tab];

            return (
                <AccordionItem
                    key={tab}
                    checked={includes(this.state.openedTabs, tab)}
                    label={getMessage(`${BASE_KEY}.tabs.${tab}`)}
                    content={(
                        <ContentComponent
                            {...this.props}
                            isNewUserForm={this.isNewUserForm}
                            onSave={this.onSaveUser}
                            isEditUserForm={this.isEditUserForm}
                            isChangePasswordForm={this.isChangePasswordForm}
                            validators={{
                                username: this.usernameError,
                                email: this.emailError,
                                handles: this.handlesError,
                                password: this.passwordError,
                            }}
                            status={this.fieldsStatus}
                        />
                    )}
                    dataLabel={tab}
                    onClick={this.setTabs(tab)}
                />
            );
        });

        if (!this.isChangePasswordForm) {
            visibleTabs.push(<FormButtons key="form-button">
                <PrimaryButton
                    additionalClass="save-button"
                    data-action="save-user"
                    onClick={this.onSaveUser}
                    disabled={!this.isSaveButtonEnabled}
                >
                    {getMessage('components.buttons.save')}
                </PrimaryButton>
                <Spacer />
                <SecondaryButton
                    data-action="cancel"
                    onClick={this.props.onCancel}
                    disabled={!this.isCancelButtonEnabled}
                >
                    {getMessage('components.buttons.cancel')}
                </SecondaryButton>
            </FormButtons>,);
        }

        return visibleTabs;
    }

    get isComponentDirty() {
        return this.props.reactForm.isDirtyFields && !this.state.justSubmitted;
    }

    get shouldRenderContent() {
        return (
            this.props.shouldRenderContent &&
      !isEmpty(this.props.reactForm.values) &&
      !this.props.user.isProcessing
        );
    }

    get deleteActionConfig() {
        const { deleteActionConfig, user, getMessage } = this.props;

        return {
            text:
        deleteActionConfig.text || getMessage('components.buttons.action_buttons.delete_user.text'),
            onClick: () => {
                deleteActionConfig.showModal(
                    { confirm: this.onDeleteUser },
                    { deleteName: user.user.username },
                );
            },
        };
    }

    render() {
        const { Breadcrumb, titleConfig, deleteActionConfig } = this.props;

        /* eslint-disable react/jsx-pascal-case */
        return (
            <PageContent>
                <ContentHeader>
                    {Breadcrumb}
                    {!this.shouldRenderContent && <LoadingIndicator isLoading orientation="left" />}
                    {titleConfig && this.shouldRenderContent && <ContentTitle {...titleConfig} />}
                </ContentHeader>
                {this.shouldRenderContent && (
                    <ContentBody>
                        {deleteActionConfig && (
                            <Actions>
                                <ACTIONS.delete {...this.deleteActionConfig} />
                            </Actions>
                        )}
                        <Accordion>
                            {this.tabs}
                        </Accordion>
                    </ContentBody>
                )}
            </PageContent>
        );
    }
}

export default AccordionView;
